import React, { useEffect, useMemo, useRef, useState } from "react";
import NarratorIntonation from "../intonation/NarratorIntonation";
import ContextHelpModal from "../ContextHelpModal";
import { loadCourse } from "../guidelines/loadCourse";

const panelTone = {
  blue: {
    button: "border-blue-200 hover:bg-blue-50 text-blue-700",
    activeWord:
      "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25 scale-[1.03]",
    idleWord:
      "bg-white/90 text-slate-800 border border-blue-100 hover:bg-white hover:border-blue-200 hover:shadow-md",
    activeView: "bg-blue-500 text-white border-blue-400 shadow",
    idleView: "bg-white/80 text-gray-700 border-white/50 hover:bg-white",
    expandBtn: "border-blue-200 text-blue-700 hover:bg-blue-50",
    metaBlock:
      "border-blue-200/80 bg-white/80 hover:bg-white text-slate-900 hover:border-blue-300",
  },
  purple: {
    button: "border-purple-200 hover:bg-purple-50 text-purple-700",
    activeWord:
      "bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/25 scale-[1.03]",
    idleWord:
      "bg-white/90 text-slate-800 border border-purple-100 hover:bg-white hover:border-purple-200 hover:shadow-md",
    activeView: "bg-purple-500 text-white border-purple-400 shadow",
    idleView: "bg-white/80 text-gray-700 border-white/50 hover:bg-white",
    expandBtn: "border-purple-200 text-purple-700 hover:bg-purple-50",
    metaBlock:
      "border-purple-200/80 bg-white/80 hover:bg-white text-slate-900 hover:border-purple-300",
  },
};

const viewOptions = [
  { key: "pitch", label: "Pitch" },
  { key: "intensity", label: "Intensity" },
  { key: "combined", label: "Combined" },
];

function IntonationFullscreenModal({
  open,
  onClose,
  tone,
  subject,
  file,
  segment,
  segments,
  currentWord,
  handleWordPlay,
  previousSegment,
  nextSegment,
  playPauseSegment,
  isPlaying,
  fullaudio,
  rate,
  setRate,
  visualizationView,
  setVisualizationView,
  handleLaneWordClick,
  broadcastTime,
  audioref,
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      const tag = e.target?.tagName;
      const isTyping =
        tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable;

      if (isTyping) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        previousSegment();
        return;
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        nextSegment();
        return;
      }

      if (e.key === " ") {
        e.preventDefault();
        playPauseSegment();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, previousSegment, nextSegment, playPauseSegment]);

  if (!open || !segment?.words?.length) return null;

  const sentenceLabel =
    segment && segments.length
      ? `Sentence ${segment.id + 1}/${segments.length}`
      : "Sentence";

  const canGoPrev = !!segment && segment.id > 0;
  const canGoNext = !!segment && segment.id < segments.length - 1;

  return (
    <div className="fixed inset-0 z-[140] bg-white">
      <div className="flex h-screen w-screen flex-col overflow-hidden">
        <div className="border-b border-slate-200 bg-white/95 backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                Intonation focus view
              </div>
              <div className="mt-1 text-base font-semibold text-slate-900">
                {sentenceLabel}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="hidden md:inline text-[11px] uppercase tracking-[0.14em] text-gray-400 mr-1">
                Speed
              </span>

              {[0.25, 0.5, 1].map((speed) => {
                const active = rate === speed;
                return (
                  <button
                    key={speed}
                    type="button"
                    onClick={() => setRate(speed)}
                    className={[
                      "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all border",
                      active ? tone.activeView : tone.idleView,
                    ].join(" ")}
                  >
                    {speed}x
                  </button>
                );
              })}

              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>

          <div className="px-4 pb-3 sm:px-6">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {viewOptions.map((opt) => {
                const active = visualizationView === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setVisualizationView(opt.key)}
                    className={[
                      "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all border",
                      active ? tone.activeView : tone.idleView,
                    ].join(" ")}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="grid h-full min-h-0 grid-rows-[auto,1fr,auto]">
            <div className="border-b border-slate-200 bg-slate-50/90 px-4 py-4 sm:px-6">
              <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
                <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-r from-slate-50 to-slate-100/80 px-4 py-4">
                  <div className="flex flex-wrap gap-2 justify-center items-center">
                    {segment.words?.map((word, i) => {
                      const isActive = currentWord === i;
                      return (
                        <span
                          key={`${segment.id}-${i}-fullscreen`}
                          onClick={() => handleWordPlay(i, word)}
                          className={[
                            "text-lg font-medium px-3 py-2 rounded-xl transition-all duration-300 cursor-pointer select-none",
                            isActive ? tone.activeWord : tone.idleWord,
                          ].join(" ")}
                          title="Play this word"
                        >
                          {word.text}
                        </span>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={playPauseSegment}
                      disabled={!segment || !fullaudio}
                      className={[
                        "rounded-xl border px-5 py-2.5 text-sm font-semibold transition-all",
                        !segment || !fullaudio
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : tone.button,
                      ].join(" ")}
                    >
                      {isPlaying ? "Pause sentence" : "Play sentence"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 overflow-hidden bg-white px-3 py-3 sm:px-6 sm:py-5">
              <div className="mx-auto h-full max-w-[1600px] rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="h-full p-2 sm:p-4">
                  <div className="h-full rounded-2xl border border-slate-200/80 bg-white overflow-hidden">
                    <div className="h-full p-2 sm:p-4">
                      <div className="h-full min-h-[320px]">
                        <NarratorIntonation
                          subject={subject}
                          file={file}
                          segment={segment}
                          onWordClick={handleLaneWordClick}
                          broadcastTime={broadcastTime}
                          audioRef={audioref}
                          view={visualizationView}
                          speed={rate}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white/95 backdrop-blur-sm px-4 py-3 sm:px-6">
              <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                  Shortcuts: ← previous, → next, Space play/pause, Esc close
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={previousSegment}
                    disabled={!canGoPrev}
                    className={[
                      "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                      !canGoPrev
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-white border ${tone.button}`,
                    ].join(" ")}
                  >
                    <span className="text-lg leading-none">←</span>
                    <span className="text-sm font-medium">Previous</span>
                  </button>

                  <button
                    type="button"
                    onClick={nextSegment}
                    disabled={!canGoNext}
                    className={[
                      "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                      !canGoNext
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : `bg-white border ${tone.button}`,
                    ].join(" ")}
                  >
                    <span className="text-sm font-medium">Next</span>
                    <span className="text-lg leading-none">→</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SimplifiedSnapsWise({
  subject,
  file,
  sectionTitle,
  accent = "blue",
  contextItem = null,
  showContextHelp = false,
  onCloseContextHelp,
}) {
  const audioref = useRef(null);
  const stopRafRef = useRef(null);
  const targetEndRef = useRef(null);

  const [segments, setSegments] = useState([]);
  const [segment, setSegment] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);
  const [broadcastTime, setBroadcastTime] = useState(0);
  const [rate, setRate] = useState(1);
  const [visualizationView, setVisualizationView] = useState("pitch");
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [course, setCourse] = useState(null);

  const tone = panelTone[accent] || panelTone.blue;

  useEffect(() => {
    const key = subject?.course_key;
    if (!key) {
      setCourse(null);
      return;
    }

    let alive = true;

    loadCourse(key)
      .then((c) => {
        if (!alive) return;
        setCourse(c || null);
      })
      .catch((err) => {
        console.warn("loadCourse failed:", err);
        if (!alive) return;
        setCourse(null);
      });

    return () => {
      alive = false;
    };
  }, [subject?.course_key]);

  const currentContextItem = useMemo(() => {
    if (contextItem) return contextItem;
    if (!course?.context || !file?.file) return null;

    return (
      course.context.find((item) => {
        const localBase = String(item?.local_audio_path || "").split("/").pop();
        const audioBase = String(item?.audio_filename || "").split("/").pop();
        const urlBase = String(item?.audio_url || "").split("/").pop();

        return (
          localBase === file.file ||
          audioBase === file.file ||
          urlBase === file.file
        );
      }) || null
    );
  }, [contextItem, course, file]);

  const fullaudio = useMemo(() => {
    if (!file) return null;

    return file?.audio_url
      ? `${process.env.REACT_APP_USER_API_URL}${file.audio_url}`
      : file?.full_path
      ? `${process.env.REACT_APP_USER_API_URL}/${file.full_path}`
      : null;
  }, [file]);

  const stopPrecisePlayback = () => {
    if (stopRafRef.current) {
      cancelAnimationFrame(stopRafRef.current);
      stopRafRef.current = null;
    }
    targetEndRef.current = null;
  };

  const playRangePrecise = (absStart, absEnd) => {
    const audio = audioref.current;
    if (!audio) return;

    stopPrecisePlayback();

    const start = Math.max(0, absStart ?? 0);
    const end = Math.max(start, absEnd ?? start);

    targetEndRef.current = end;
    audio.currentTime = start;
    audio.playbackRate = rate;
    audio.play();
    setIsPlaying(true);

    const tick = () => {
      const a = audioref.current;
      const targetEnd = targetEndRef.current;
      if (!a || targetEnd == null) return;

      if (a.currentTime >= targetEnd) {
        a.pause();
        setIsPlaying(false);
        stopPrecisePlayback();
        return;
      }

      stopRafRef.current = requestAnimationFrame(tick);
    };

    stopRafRef.current = requestAnimationFrame(tick);
  };

  const getSegments = async ({ subject, file }) => {
    const outdata = {
      author: subject.author,
      title: subject.title,
      language: subject.language,
      file,
      type: subject.type,
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/whisper/get-segments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(outdata),
        }
      );

      if (!response.ok) {
        console.warn("getSegments failed:", response.status);
        return;
      }

      const json = await response.json();
      setSegments(Array.isArray(json.segments) ? json.segments : []);
    } catch (e) {
      console.error("getSegments error:", e);
    }
  };

  const syncCurrentWord = () => {
    const audio = audioref.current;
    if (!audio || !segment?.words?.length) return;

    setBroadcastTime(audio.currentTime - (segment.start || 0));

    const found = segment.words.findIndex((word, i) => {
      const isLast = i === segment.words.length - 1;
      return isLast
        ? word.start <= audio.currentTime && word.end >= audio.currentTime
        : word.start <= audio.currentTime && word.end > audio.currentTime;
    });

    if (found !== -1) {
      setCurrentWord(found);
    }
  };

  const playSegment = (seg) => {
    if (!seg) return;
    setSegment(seg);
    setCurrentWord(0);
    playRangePrecise(seg.start, seg.end);
  };

  const handleWordPlay = (wordIndex, word) => {
    if (!segment || !word) return;
    setCurrentWord(wordIndex);
    playRangePrecise(word.start, word.end);
  };

  const handleLaneWordClick = (word) => {
    if (!word) return;
    const idx = segment?.words?.findIndex(
      (w) =>
        w.start === word.start &&
        w.end === word.end &&
        w.text === word.text
    );
    handleWordPlay(idx >= 0 ? idx : 0, word);
  };

  const playPauseSegment = () => {
    const audio = audioref.current;
    if (!audio || !segment) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      stopPrecisePlayback();
      return;
    }

    playSegment(segment);
  };

  const previousSegment = () => {
    if (!segment || !segments.length) return;
    const prev = segments[Math.max(0, segment.id - 1)];
    setSegment(prev);
    setCurrentWord(0);
    stopPrecisePlayback();
    if (audioref.current) {
      audioref.current.pause();
      audioref.current.currentTime = prev.start;
      setBroadcastTime(0);
      setIsPlaying(false);
    }
  };

  const nextSegment = () => {
    if (!segment || !segments.length) return;
    const next = segments[Math.min(segments.length - 1, segment.id + 1)];
    setSegment(next);
    setCurrentWord(0);
    stopPrecisePlayback();
    if (audioref.current) {
      audioref.current.pause();
      audioref.current.currentTime = next.start;
      setBroadcastTime(0);
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    setSegments([]);
    setSegment(null);
    setCurrentWord(0);
    setIsPlaying(false);
    setBroadcastTime(0);
    stopPrecisePlayback();

    if (file?.file) {
      getSegments({ subject, file: file.file });
    }
  }, [file, subject]);

  useEffect(() => {
    if (segments.length > 0) {
      setSegment(segments[0]);
      setCurrentWord(0);
      setBroadcastTime(0);
    }
  }, [segments]);

  useEffect(() => {
    const audio = audioref.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      syncCurrentWord();
    };

    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("play", onPlay);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("play", onPlay);
    };
  }, [segment]);

  useEffect(() => {
    if (audioref.current) {
      audioref.current.playbackRate = rate;
    }
  }, [rate]);

  useEffect(() => {
    return () => {
      stopPrecisePlayback();
    };
  }, []);

  if (!segment?.words?.length) {
    return (
      <div className="relative rounded-3xl border border-white/40 bg-white/80 shadow-lg overflow-hidden">
        <div className="p-4 text-sm text-slate-500">Loading segment data…</div>

        <ContextHelpModal
          open={showContextHelp}
          onClose={onCloseContextHelp}
          course={course}
          contextItem={currentContextItem}
          initialTab="overview"
          contained
        />
      </div>
    );
  }

  const sentenceLabel =
    segment && segments.length
      ? `Sentence ${segment.id + 1}/${segments.length}`
      : "Sentence";

  return (
    <>
      <div className="relative rounded-3xl border border-white/40 bg-white/80 backdrop-blur-sm shadow-lg overflow-hidden">
        <audio
          ref={audioref}
          src={fullaudio || undefined}
          preload="metadata"
          className="hidden"
        />

        <div className="p-4 sm:p-5 space-y-5">
          {!fullaudio ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Missing `audio_url` or `full_path` on selected file.
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={previousSegment}
              disabled={!segment || segment.id === 0}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                !segment || segment.id === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : `bg-white/70 border ${tone.button}`,
              ].join(" ")}
            >
              <span className="text-lg leading-none">←</span>
              <span className="text-sm font-medium">Previous</span>
            </button>

            <div className="flex-1 min-w-0 h-10 flex items-center justify-center">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {sentenceLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={nextSegment}
              disabled={!segment || segment.id >= segments.length - 1}
              className={[
                "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
                !segment || segment.id >= segments.length - 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : `bg-white/70 border ${tone.button}`,
              ].join(" ")}
            >
              <span className="text-sm font-medium">Next</span>
              <span className="text-lg leading-none">→</span>
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-r from-slate-50 to-slate-100/70 px-4 py-4">
            <div className="flex flex-wrap gap-2 justify-center items-center">
              {segment.words?.map((word, i) => {
                const isActive = currentWord === i;
                return (
                  <span
                    key={`${segment.id}-${i}`}
                    onClick={() => handleWordPlay(i, word)}
                    className={[
                      "text-lg font-medium px-3 py-2 rounded-xl transition-all duration-300 cursor-pointer select-none",
                      isActive ? tone.activeWord : tone.idleWord,
                    ].join(" ")}
                    title="Play this word"
                  >
                    {word.text}
                  </span>
                );
              })}
            </div>

            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={playPauseSegment}
                disabled={!segment || !fullaudio}
                className={[
                  "rounded-xl border px-5 py-2.5 text-sm font-semibold transition-all",
                  !segment || !fullaudio
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : tone.button,
                ].join(" ")}
              >
                {isPlaying ? "Pause sentence" : "Play sentence"}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {viewOptions.map((opt) => {
                const active = visualizationView === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setVisualizationView(opt.key)}
                    className={[
                      "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all border",
                      active ? tone.activeView : tone.idleView,
                    ].join(" ")}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-[11px] uppercase tracking-[0.14em] text-gray-400 mr-1">
                Speed
              </span>

              {[0.25, 0.5, 1].map((speed) => {
                const active = rate === speed;

                return (
                  <button
                    key={`speed-${speed}`}
                    type="button"
                    onClick={() => setRate(speed)}
                    className={[
                      "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all border",
                      active ? tone.activeView : tone.idleView,
                    ].join(" ")}
                  >
                    {speed}x
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => setIsFullscreenOpen(true)}
                className={[
                  "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                  tone.expandBtn,
                ].join(" ")}
              >
                Full screen
              </button>
            </div>
          </div>

          <section className="rounded-3xl border border-white/40 bg-white/70 backdrop-blur-sm shadow-lg shadow-blue-500/5 overflow-hidden">
            <div className="p-3 sm:p-4">
              <div className="rounded-2xl border border-white/40 bg-white/60 overflow-hidden">
                <div className="sm:p-3">
                  <button
                    type="button"
                    onClick={() => setIsFullscreenOpen(true)}
                    className="block w-full text-left"
                    title="Open intonation chart in fullscreen"
                  >
                    <div className="h-[250px] sm:h-[270px]">
                      <NarratorIntonation
                        subject={subject}
                        file={file}
                        segment={segment}
                        onWordClick={handleLaneWordClick}
                        broadcastTime={broadcastTime}
                        audioRef={audioref}
                        view={visualizationView}
                        speed={rate}
                      />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>

        <ContextHelpModal
          open={showContextHelp}
          onClose={onCloseContextHelp}
          course={course}
          contextItem={currentContextItem}
          initialTab="overview"
          contained
        />
      </div>

      <IntonationFullscreenModal
        open={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        tone={tone}
        subject={subject}
        file={file}
        segment={segment}
        segments={segments}
        currentWord={currentWord}
        handleWordPlay={handleWordPlay}
        previousSegment={previousSegment}
        nextSegment={nextSegment}
        playPauseSegment={playPauseSegment}
        isPlaying={isPlaying}
        fullaudio={fullaudio}
        rate={rate}
        setRate={setRate}
        visualizationView={visualizationView}
        setVisualizationView={setVisualizationView}
        handleLaneWordClick={handleLaneWordClick}
        broadcastTime={broadcastTime}
        audioref={audioref}
      />
    </>
  );
}